import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import api from "../utils/api";
import {
  UserGoal,
  CustomerData,
  BenefitsSummary,
  BenefitsBreakdown,
  CreditEligibility,
} from "../utils/types";
import { UIUtils } from "../utils/uiUtils";
import { useAuth } from "../utils/auth";

interface UserContextType {
  user: CustomerData["basic_profile"] | null;
  userGoal: UserGoal | null;
  benefitsSummary: BenefitsSummary | null;
  benefitsBreakdown: BenefitsBreakdown | null;
  creditEligibility: CreditEligibility | null;
  marketing_email_dnc_flag: boolean;
  marketing_sms_dnc_flag: boolean;
  guarantorEarlyAccess?: {
    is_subscribed: boolean;
    source?: string;
    subscribed_date?: string;
  };
  isConsentAccepted: boolean;
  categories: number[];
  appliedBenefits: string[];
  isLoading: boolean;
  error: string | null;
  updateUserData: (data: {
    user: any;
    userGoal: any;
    benefitsSummary: BenefitsSummary | null;
    benefitsBreakdown: BenefitsBreakdown | null;
    creditEligibility?: CreditEligibility | null;
    marketing_email_dnc_flag?: boolean;
    marketing_sms_dnc_flag?: boolean;
    guarantorEarlyAccess?: {
      is_subscribed: boolean;
      source?: string;
      subscribed_date?: string;
    };
  }) => void;
  updateConsent: (consent: boolean) => Promise<void>;
  clearUserData: () => void;
  setDiscountsForGoal: (goalId: number, categories?: number[]) => void;
  setSaveOnShopping: () => void;
  setCategories: (categories: number[]) => void;
  toggleBenefitApplied: (benefitId: string) => void;
}

const USER_STORAGE_KEY = "afforda_user_data";
const TOKEN_STORAGE_KEY = "afforda_auth_token";
const APPLIED_FILTERS_KEY = "afforda_applied_filters";

const UserContext = createContext<UserContextType | undefined>(undefined);

// The email address is personal data, and localStorage is readable by any script
// on the page. The address stays in memory for the current tab, but it must not
// persist to disk. A page that needs it calls api.fetchUserData() instead.
//
// The rest of the cached state stays, because the pages need it to render on a
// cold load without a blank first paint.
const persistUserData = (data: object) => {
  const { user, ...rest } = data as { user?: Record<string, unknown> | null };

  const safeData = {
    ...rest,
    user: user ? { ...user, email: undefined } : user,
  };

  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(safeData));
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user: authUser } = useAuth();

  const [userData, setUserData] = useState<
    Omit<
      UserContextType,
      | "updateUserData"
      | "updateConsent"
      | "clearUserData"
      | "setDiscountsForGoal"
      | "setSaveOnShopping"
      | "setCategories"
      | "toggleBenefitApplied"
    >
  >(() => {
    try {
      const storedData = localStorage.getItem(USER_STORAGE_KEY);
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);

      if (token && !storedData) {
        return {
          user: null,
          userGoal: null,
          benefitsSummary: null,
          benefitsBreakdown: null,
          creditEligibility: null,
          marketing_email_dnc_flag: false,
          marketing_sms_dnc_flag: false,
          guarantorEarlyAccess: undefined,
          isConsentAccepted: false,
          categories: [],
          isLoading: false,
          error: null,
        };
      }

      const parsedData = storedData
        ? JSON.parse(storedData)
        : {
            user: null,
            userGoal: null,
            benefitsSummary: null,
            benefitsBreakdown: null,
            creditEligibility: null,
            marketing_email_dnc_flag: false,
            marketing_sms_dnc_flag: false,
            isConsentAccepted: false,
            guarantorEarlyAccess: undefined,
            categories: [],
            isLoading: false,
            error: null,
          };

      // Extract applied benefits from stored benefitsBreakdown if available
      const appliedBenefitsFromStorage: string[] = [];
      if (parsedData.benefitsBreakdown) {
        const breakdown = parsedData.benefitsBreakdown;
        const categoryKeys = [
          "month",
          "energySupportSocialTariffs",
          "discretionary",
          "lumpSum",
          "homeImprovementsGrants",
          "other",
        ] as const;

        categoryKeys.forEach((category) => {
          if (breakdown[category] && Array.isArray(breakdown[category])) {
            breakdown[category]!.forEach((benefit: any) => {
              if (benefit.applied === true) {
                appliedBenefitsFromStorage.push(benefit.id);
              }
            });
          }
        });
      }

      return {
        ...parsedData,
        marketing_sms_dnc_flag: parsedData.marketing_sms_dnc_flag ?? false,
        appliedBenefits: appliedBenefitsFromStorage,
      };
    } catch (error) {
      UIUtils.trackException("Reading from localStorage", error);
      return {
        user: null,
        userGoal: null,
        benefitsSummary: null,
        benefitsBreakdown: null,
        creditEligibility: null,
        marketing_email_dnc_flag: false,
        marketing_sms_dnc_flag: false,
        guarantorEarlyAccess: undefined,
        isConsentAccepted: false,
        categories: [],
        appliedBenefits: [],
        isLoading: false,
        error: null,
      };
    }
  });

  const updateUserData = useCallback(
    (data: {
      user: any;
      userGoal: any;
      benefitsSummary: BenefitsSummary | null;
      benefitsBreakdown: BenefitsBreakdown | null;
      creditEligibility?: CreditEligibility | null;
      marketing_email_dnc_flag?: boolean;
      marketing_sms_dnc_flag?: boolean;
      guarantorEarlyAccess?: {
      is_subscribed: boolean;
      source?: string;
      subscribed_date?: string;
    };
    }) => {
      // Check for stored filters
      const storedFilters = localStorage.getItem(APPLIED_FILTERS_KEY);
      const categories = storedFilters
        ? JSON.parse(storedFilters)
        : userData.categories;

      // Extract applied benefits from backend data (source of truth)
      const appliedBenefitsFromBackend: string[] = [];
      if (data.benefitsBreakdown) {
        const breakdown = data.benefitsBreakdown;
        const categoryKeys = [
          "month",
          "energySupportSocialTariffs",
          "discretionary",
          "lumpSum",
          "homeImprovementsGrants",
          "other",
        ] as const;

        categoryKeys.forEach((category) => {
          if (breakdown[category] && Array.isArray(breakdown[category])) {
            breakdown[category]!.forEach((benefit: any) => {
              if (benefit.applied === true) {
                appliedBenefitsFromBackend.push(benefit.id);
              }
            });
          }
        });
      }

      // Use backend data as the only source of truth
      const appliedBenefits = appliedBenefitsFromBackend;

      const newData = {
        user: data.user,
        userGoal: data.userGoal,
        benefitsSummary: data.benefitsSummary,
        benefitsBreakdown: data.benefitsBreakdown,
        creditEligibility:
          data.creditEligibility !== undefined
            ? data.creditEligibility
            : userData.creditEligibility,
        marketing_email_dnc_flag:
          typeof data.marketing_email_dnc_flag === "boolean"
            ? data.marketing_email_dnc_flag
            : userData.marketing_email_dnc_flag,
        marketing_sms_dnc_flag:
          typeof data.marketing_sms_dnc_flag === "boolean"
            ? data.marketing_sms_dnc_flag
            : userData.marketing_sms_dnc_flag,
        guarantorEarlyAccess: data.guarantorEarlyAccess !== undefined ? data.guarantorEarlyAccess : userData.guarantorEarlyAccess,
        isConsentAccepted: userData.isConsentAccepted,
        categories,
        appliedBenefits,
        isLoading: false,
        error: null,
      };
      setUserData(newData);
      try {
        persistUserData(newData);
      } catch (error) {
        UIUtils.trackException("Saving to localStorage", error);
      }
    },
    [userData],
  );

  // Fetch user data when authenticated but no user data exists.
  //
  // The cached copy holds no email address, because persistUserData removes it.
  // So this effect also runs when the cached user has no email. The ref stops a
  // repeat fetch if the response itself has no email address.
  const hasRefetchedForEmail = useRef(false);

  useEffect(() => {
    const needsEmail =
      !!userData.user && !userData.user.email && !hasRefetchedForEmail.current;

    const fetchUserDataIfNeeded = async () => {
      if (authUser?.accessToken && (!userData.user || needsEmail)) {
        if (needsEmail) {
          hasRefetchedForEmail.current = true;
        }
        try {
          const response = await api.fetchUserData();
          if (response.data) {
            updateUserData({
              user: response.data.basic_profile,
              userGoal: response.data.goal_data,
              benefitsSummary: response.data.benefits_summary,
              benefitsBreakdown: response.data.benefits_breakdown,
              creditEligibility:
                response.data.loan_data?.credit_eligibility ?? null,
              marketing_email_dnc_flag:
                response.data.dnc_preferences?.marketing_email_dnc_flag ??
                false,
              marketing_sms_dnc_flag:
                response.data.dnc_preferences?.marketing_sms_dnc_flag ??
                false,
            });
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      }
    };

    fetchUserDataIfNeeded();
  }, [authUser?.accessToken, userData.user, updateUserData]);

  // Clear the guard on sign-out, so the next session can fetch the email again.
  useEffect(() => {
    if (!authUser?.accessToken) {
      hasRefetchedForEmail.current = false;
    }
  }, [authUser?.accessToken]);

  const updateConsent = async (consent: boolean) => {
    try {
      await api.capturePrivacyPolicyConsent();
      const newData = {
        ...userData,
        isConsentAccepted: consent,
      };
      setUserData(newData);
      persistUserData(newData);
    } catch (error) {
      UIUtils.trackException("Update consent", error);
      throw error;
    }
  };

  const clearUserData = () => {
    const clearedData = {
      user: null,
      userGoal: null,
      benefitsSummary: null,
      benefitsBreakdown: null,
      creditEligibility: null,
      marketing_email_dnc_flag: false,
      marketing_sms_dnc_flag: false,
      isConsentAccepted: false,
      categories: [],
      appliedBenefits: [],
      isLoading: false,
      error: null,
    };
    setUserData(clearedData);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem("afforda_form_data");
  };

  const setCategories = (categories: number[]) => {
    const newData = {
      ...userData,
      categories,
    };
    setUserData(newData);
    try {
      persistUserData(newData);
    } catch (error) {
      UIUtils.trackException("Saving to localStorage", error);
    }
  };

  const setDiscountsForGoal = async (goalId: number, categories?: number[]) => {
    try {
      if (categories) {
        setCategories(categories);
      }
    } catch (error) {
      UIUtils.trackException("Set discounts for goal", error);
    }
  };

  const setSaveOnShopping = async () => {
    try {
      // Set default category to [1] for Groceries and supermarkets
      setCategories([1]);
    } catch (error) {
      UIUtils.trackException("Set saveOnShopping", error);
    }
  };

  const toggleBenefitApplied = async (benefitId: string) => {
    if (!userData.user) return;

    const isCurrentlyApplied = userData.appliedBenefits.includes(benefitId);
    const newApplied = !isCurrentlyApplied;

    try {
      // Call backend API
      await api.toggleAppliedBenefit(benefitId, newApplied);

      // Update local state optimistically
      const newAppliedBenefits = newApplied
        ? [...userData.appliedBenefits, benefitId]
        : userData.appliedBenefits.filter((id) => id !== benefitId);

      // Also update the benefitsBreakdown to reflect the change
      const updatedBreakdown = userData.benefitsBreakdown
        ? { ...userData.benefitsBreakdown }
        : null;
      if (updatedBreakdown) {
        const categories = [
          "month",
          "energySupportSocialTariffs",
          "discretionary",
          "lumpSum",
          "homeImprovementsGrants",
          "other",
        ] as const;
        categories.forEach((category) => {
          if (
            updatedBreakdown[category] &&
            Array.isArray(updatedBreakdown[category])
          ) {
            updatedBreakdown[category] = updatedBreakdown[category]!.map(
              (benefit: any) =>
                benefit.id === benefitId
                  ? { ...benefit, applied: newApplied }
                  : benefit,
            );
          }
        });
      }

      const newData = {
        ...userData,
        appliedBenefits: newAppliedBenefits,
        benefitsBreakdown: updatedBreakdown,
      };
      setUserData(newData);

      // Update localStorage as cache
      persistUserData(newData);
    } catch (error) {
      UIUtils.trackException("Toggle applied benefit", error);
      UIUtils.showMessageOnSnackBar(
        "Failed to update benefit status. Please try again.",
      );
    }
  };

  return (
    <UserContext.Provider
      value={{
        ...userData,
        updateUserData,
        updateConsent,
        clearUserData,
        setDiscountsForGoal,
        setSaveOnShopping,
        setCategories,
        toggleBenefitApplied,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
